// API client for test-recording metadata + sharing.
// Recordings are addressed by (projectId, recordingId). Metadata (title, share
// state, etc.) lives on the backend `test_recordings` document.
import api from '../utils/axios';

export interface TestRecordingDetails {
  recording_id: string;
  project_id: string;
  message_id: string;
  thread_id: string;
  filename: string;
  batch_name: string | null;
  title: string;
  readonly_sharable_link_enabled: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const recordingBasePath = (projectId: string, recordingId: string) =>
  `/browser-stream/recordings/${encodeURIComponent(projectId)}/${encodeURIComponent(recordingId)}`;

// Send auth only when a token exists — the public viewer page calls this
// anonymously for shared recordings.
const optionalAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Full metadata for a recording. Used by the share panel and the public viewer page. */
export async function getRecordingDetails(
  projectId: string,
  recordingId: string,
): Promise<TestRecordingDetails> {
  const response = await api.get(`${recordingBasePath(projectId, recordingId)}/details`, {
    headers: optionalAuthHeader(),
  });
  return response.data as TestRecordingDetails;
}

export async function updateRecordingShareState(
  projectId: string,
  recordingId: string,
  enabled: boolean,
): Promise<boolean> {
  const response = await api.put(
    `${recordingBasePath(projectId, recordingId)}/share`,
    { readonly_sharable_link_enabled: enabled },
    { headers: optionalAuthHeader() },
  );
  return Boolean(response.data?.readonly_sharable_link_enabled);
}
